import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import TrimbleAddressAutocomplete from '../TrimbleAddressAutocomplete';

const TYPES = ['Customer', 'Shipper', 'Consignee', 'Driver', 'Maintenance Vendor', 'Carrier', 'Other'];

export default function ContactFormModal({ contact, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '',
    type: 'Customer',
    contact: '',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    email: '',
    primary_phone: '',
    alternate_phone: '',
    alternate_phone2: '',
    fax: '',
    motor_carrier_number: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (contact) {
      setForm(contact);
    }
  }, [contact]);


  const set = (key, value) => setForm(p => ({ ...p, [key]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Company name is required.');
      return;
    }
    setSaving(true);
    try {
      if (contact?.id) {
        await client.entities.Contact.update(contact.id, form);
        toast.success('Contact updated.');
        onSaved(form);
      } else {
        const result = await client.entities.Contact.create(form);
        toast.success('Contact created.');
        onSaved(result);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-2xl my-8 z-10 p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="font-space font-bold text-foreground text-lg">
            {contact ? 'Edit Address' : 'Add Address'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">Company Name *</label>
              <Input
                value={form.name}
                onChange={e => set('name', e.target.value)}
                placeholder="Company name"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">Type *</label>
              <select
                value={form.type}
                onChange={e => set('type', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm"
              >
                {TYPES.map(t => (
                  <option key={t} value={t} className="bg-background">
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Contact Person</label>
            <Input
              value={form.contact}
              onChange={e => set('contact', e.target.value)}
                placeholder="Contact person name"
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Street Address</label>
            <TrimbleAddressAutocomplete
              value={form.street}
              onChange={e => set('street', e.target.value)}
              onSelect={data => {
                set('street', data.street || '');
                set('city', data.city || '');
                set('state', data.state || '');
                set('zip_code', data.zip || '');
              }}
              label=""
              placeholder="Street address"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">City</label>
              <Input
                value={form.city}
                onChange={e => set('city', e.target.value)}
                placeholder="City"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">State</label>
              <Input
                value={form.state}
                onChange={e => set('state', e.target.value)}
                placeholder="State"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">ZIP Code</label>
              <Input
                value={form.zip_code}
                onChange={e => set('zip_code', e.target.value)}
                placeholder="ZIP"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Email</label>
            <Input
              type="email"
              value={form.email}
              onChange={e => set('email', e.target.value)}
                placeholder="Email"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">Primary Phone</label>
              <Input
                value={form.primary_phone}
                onChange={e => set('primary_phone', e.target.value)}
                placeholder="Phone"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">Alternate Phone</label>
              <Input
                value={form.alternate_phone}
                onChange={e => set('alternate_phone', e.target.value)}
                placeholder="Alternate phone"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">Alternate Phone 2</label>
              <Input
                value={form.alternate_phone2}
                onChange={e => set('alternate_phone2', e.target.value)}
                placeholder="Alternate phone 2"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1.5">Fax</label>
              <Input
                value={form.fax}
                onChange={e => set('fax', e.target.value)}
                placeholder="Fax"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Motor Carrier Number</label>
            <Input
              value={form.motor_carrier_number}
              onChange={e => set('motor_carrier_number', e.target.value)}
                placeholder="MC Number"
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground block mb-1.5">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary/50"
              placeholder="Notes"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground">
            {saving ? 'Saving...' : contact ? 'Save Changes' : 'Add Address'}
          </Button>
          <Button onClick={onClose} variant="outline" className="border-border">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}